import { useState, useRef, useEffect } from "react";
import classNames from "classnames";

const AsyncSearchSelect = ({
  label,
  name,
  value,
  onChange,
  fetchOptions,         
  placeholder = "Select",
  disabled = false,
  error,
  serverError,
  size = "sm",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // 🔍 backend search with debounce
  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!search) {
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        const result = await fetchOptions(search);
        setOptions(result || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [search, isOpen]);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  const sizeClasses = classNames({
    "px-3 py-2 text-xs": size === "sm",
    "px-4 py-3 text-sm": size === "md",
  });

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && (
        <label className="block mb-1 text-sm font-medium">{label}</label>
      )}

      <div
        className={classNames(
          "border rounded cursor-pointer bg-white",
          sizeClasses,
          {
            "border-gray-300": !error,
            "border-red-500": error,
            "bg-gray-100 cursor-not-allowed": disabled,
          }
        )}
        onClick={() => !disabled && setIsOpen((p) => !p)}
      >
        <span className="block truncate">
          {selected?.label || placeholder}
        </span>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
          <input
            className="w-full px-3 py-2 text-sm border-b"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {loading && (
            <div className="px-3 py-2 text-xs text-gray-400">
              Searching...
            </div>
          )}

          {!loading && options.length === 0 && search && (
            <div className="px-3 py-2 text-xs text-gray-400">
              No results
            </div>
          )}

          {options.map((opt) => (
            <div
              key={opt.value}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
              onClick={() => {
                onChange({
                  target: { name, value: opt.value },
                });
                setIsOpen(false);
                setSearch("");
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}

      {(error || serverError) && (
        <div className="text-xs text-red-500 mt-1">
          {error || serverError}
        </div>
      )}
    </div>
  );
};

export default AsyncSearchSelect;
